import { zodResolver } from '@hookform/resolvers/zod';
import { Spinner } from 'baseui/spinner';
import { useEffect, useRef, useState } from 'react';
import { AlertCircle, CheckCircle, Map } from 'react-feather';
import {
  Control,
  FieldPath,
  FieldValues,
  get,
  useForm,
  useFormState,
  useWatch,
} from 'react-hook-form';
import { z } from 'zod';

import { Button } from 'components/Button';
import { FormController } from 'components/FormController';
import { Input } from 'components/Input';
import { CountrySelect } from 'features/CountrySelect';
import { StateSelect } from 'features/StateSelect';
import { MapPicker, MapPickerLocation } from 'features/work-requests/MapPicker';
import { getErrorMessage, isMutationSuccess } from 'services/api/base-api';
import { Address, rawAddressSchema } from 'services/api/types.shared';
import { endpoints as UtilityAPIService } from 'services/api/utilities/endpoint';
import { store } from 'store/store';
import { useLocale } from 'utils/hooks/useLocale';

const nonEmptyString = z
  .string()
  .trim()
  .min(1, { message: 'errors.invalid_type_received_undefined' });

const addressSchema = z.object({
  street: nonEmptyString,
  street2: z.string().trim().nullable().optional().default(''),
  city: nonEmptyString,
  state: nonEmptyString,
  postalCode: nonEmptyString,
  country: nonEmptyString,
  lng: z.number().refine(
    (val) => {
      return val !== 0;
    },
    { message: 'No coordinates set' },
  ),
  lat: z.number().refine((val) => val !== 0, { message: 'No coordinates set' }),
  isVerified: z.boolean().optional(),
  isVerifiying: z.boolean().optional(),
  timeZone: z.string().trim().nullable().optional().default('')
});

type FormFields = z.infer<typeof addressSchema>;

/**
 * https://github.com/react-hook-form/react-hook-form/issues/1005#issuecomment-988380981
 */
const stopPropagate = (callback: () => void) => {
  return (e: { stopPropagation: () => void; preventDefault: () => void }) => {
    e.stopPropagation();
    e.preventDefault();
    callback();
  };
};

const modifiableAddressSchema = rawAddressSchema.pick({
  street: true,
  street2: true,
  city: true,
  state: true,
  postalCode: true,
  country: true,
});
type AddressLike = z.infer<typeof modifiableAddressSchema>;

function debounce<T extends (...args: any[]) => void>(func: T, wait: number) {
  let timeout: ReturnType<typeof setTimeout>;
  return function executedFunction(...args: any[]) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

const geocodeAddress = async (address: Address | AddressLike) => {
  const formattedAddres = `${address.street} ${address.city} ${address.state} ${address.country}`;

  const result = await store.dispatch(
    UtilityAPIService.endpoints.geocode.initiate({ address: formattedAddres }),
  );
  if (isMutationSuccess(result)) {
    if (result.data.confidence >= 0.8) {
      return {
        latitude: result.data.latitude,
        longitude: result.data.longitude,
        timeZone: result.data.timeZone
      };
    }
    throw new Error('No confident location found');
  } else {
    throw new Error(getErrorMessage(result.error));
  }
};

export default function ServiceLocationAddressForm({
  id,
  initialValues,
  onSubmit: onSubmitHandler,
}: {
  id?: string;
  initialValues?: Partial<FormFields>;
  onSubmit: (data: FormFields) => void;
}) {
  const { t } = useLocale();
  const [showMapPicker, setShowMapPicker] = useState(false);
  const {
    control,
    handleSubmit,
    setValue,
    watch,
    clearErrors,
    setError,
    getValues,
  } = useForm<FormFields>({
    defaultValues: initialValues,
    resolver: zodResolver(addressSchema),
  });
  const onSubmit = handleSubmit((data) => {
    onSubmitHandler(data);
  });
  const isAddressVerified = watch('isVerified');
  const isVerifyingAddress = watch('isVerifiying');
  const selectedCountryCode = watch('country');
  const currentLat = watch('lat');
  const currentLng = watch('lng');
  const normalizedCurrentLat = Number(currentLat);
  const normalizedCurrentLng = Number(currentLng);
  const currentInitialLocation =
    Number.isFinite(normalizedCurrentLat) && Number.isFinite(normalizedCurrentLng)
      ? { lat: normalizedCurrentLat, lng: normalizedCurrentLng }
      : undefined;
  const mapSelectedAddressCoordinates = useRef<{ lat: number; lng: number } | null>(null);

  const handleMapSelect = (location: MapPickerLocation) => {
    setValue('street', location.street);
    setValue('street2', location.street2);
    setValue('city', location.city);
    setValue('state', location.state);
    setValue('postalCode', location.postalCode);
    setValue('country', location.country);
    setValue('lat', location.lat);
    setValue('lng', location.lng);
    setValue('timeZone', location.timeZone);
    setValue('isVerified', true);
    mapSelectedAddressCoordinates.current = {
      lat: location.lat,
      lng: location.lng,
    };
    clearErrors();
    setShowMapPicker(false);
  };

  useEffect(() => {
    const callback = debounce(
      async (
        data: Parameters<Parameters<typeof watch>[0]>[0],
        { name }: Parameters<Parameters<typeof watch>[0]>[1],
      ) => {
        const keysToMatch: FieldPath<FormFields>[] = [
          'street',
          'city',
          'state',
          'postalCode',
          'country',
        ];
        const isAddress = name && keysToMatch.includes(name);
        const validated = modifiableAddressSchema.safeParse(data);
        const hasMapSelectedCoordinates =
          !!mapSelectedAddressCoordinates.current &&
          data.lat === mapSelectedAddressCoordinates.current.lat &&
          data.lng === mapSelectedAddressCoordinates.current.lng &&
          data.lat !== 0 &&
          data.lng !== 0;
        if (isAddress && hasMapSelectedCoordinates) {
          return;
        }
        if (isAddress && validated.success) {
          setValue('isVerifiying', true);
          setValue('isVerified', false);
          clearErrors(['lat', 'lng', 'isVerified']);
          geocodeAddress(validated.data)
            .then((coordinates) => {
              clearErrors('isVerified');
              setValue('isVerified', true);
              setValue('lat', coordinates.latitude);
              setValue('lng', coordinates.longitude);
              setValue('timeZone', coordinates.timeZone)
            })
            .catch(() => {
              clearErrors('lat');
              clearErrors('lng');
              setValue('lat', 0);
              setValue('lng', 0);
              setValue('timeZone', '')
              setValue('isVerified', false);
              setError('isVerified', {
                // message:
                //   'The address you entered did not return any verified locations.',
                message: t('address_not_verified_message'),
              });
            })
            .finally(() => setValue('isVerifiying', false));
        }
      },
      950,
    );
    const { unsubscribe } = watch(callback);
    return () => unsubscribe();
  }, [watch, setValue, clearErrors, setError, t]);

  return (
    <>
      {showMapPicker && (
        <MapPicker
          onSelect={handleMapSelect}
          onClose={() => setShowMapPicker(false)}
          initialLocation={currentInitialLocation}
        />
      )}
      <form
        className="grid grid-cols-4 gap-x-4"
        id={id}
        onSubmit={stopPropagate(onSubmit)}
      >
        <div className="col-span-full mb-3">
          <Button
            type="button"
            kind="secondary"
            size="compact"
            onClick={() => setShowMapPicker(true)}
            startEnhancer={<Map className="h-4 w-4" />}
          >
            {t('Select from Map')}
          </Button>
        </div>
        <div className="col-span-full">
          {isAddressVerified && (
            <div className="flex items-center space-x-1 text-green-600">
              <span>
                <CheckCircle className="h-4 w-4" />
              </span>
              <p className="text-sm">{t('Verified')}</p>
            </div>
          )}
          <VerfyingAddress control={control} path="isVerifiying" />
          <RHFErrorMessageValue control={control} field="lat">
          {() => (
            // <div className="flex items-start space-x-2 rounded bg-red-50 px-4 py-2 text-sm font-medium text-red-600">
            //   <span>
            //     <AlertCircle className="h-5 w-5" />
            //   </span>
            //   <div className="space-y-2">
            //     <p>{t('Address not verified')}</p>
            //     <Button
            //       size="mini"
            //       shape="pill"
            //       className="px-4"
            //       type="button"
            //       onClick={() => {
            //         setValue('street', getValues('street'));
            //       }}
            //       isLoading={isVerifyingAddress}
            //     >
            //       {t('Verify Address')}
            //     </Button>
            //   </div>
            // </div>
            <Alert>
              <AlertTitle>{t('Address not verified')}</AlertTitle>
              <AlertDescription>
                {t('address_not_verified_message')}
              </AlertDescription>
              <AlertActions>
                <button
                  type="button"
                  className="rounded-md bg-red-50 px-2 py-1.5 text-sm font-medium text-red-800 hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-red-600 focus:ring-offset-2 focus:ring-offset-red-50"
                  onClick={() => {
                    setValue('street', getValues('street'));
                  }}
                  disabled={isVerifyingAddress}
                >
                  {t('Verify Address')}
                </button>
              </AlertActions>
            </Alert>
          )}
        </RHFErrorMessageValue>
        <RHFErrorMessageValue control={control} field="isVerified">
          {(message) => (
            <Alert>
              <AlertTitle>{t('Address not verified')}</AlertTitle>
              <AlertDescription>
                {t('address_not_verified_message')}
              </AlertDescription>
            </Alert>
          )}
        </RHFErrorMessageValue>
      </div>
      <FormController
        control={control}
        name="street"
        label={t('Address')}
        overrides={{ Root: { props: { className: 'col-span-3' } } }}
      >
        {({ field }) => <Input {...field} value={field.value ?? ''} />}
      </FormController>
      <FormController
        control={control}
        name="street2"
        label={t('Unit')}
        overrides={{ Root: { props: { className: 'col-span-1' } } }}
        defaultValue={''}
      >
        {({ field }) => <Input {...field} value={field.value ?? ''} />}
      </FormController>
      <FormController
        control={control}
        name="city"
        label={t('City')}
        overrides={{ Root: { props: { className: 'col-span-2' } } }}
      >
        {({ field }) => <Input {...field} value={field.value ?? ''} />}
      </FormController>
      <FormController
        control={control}
        name="country"
        label={t('Country')}
        overrides={{ Root: { props: { className: 'col-span-2' } } }}
      >
        {({ field }) => (
          <CountrySelect
            value={field.value}
            onChange={({ option }) => field.onChange(option?.id ?? '')}
          />
        )}
      </FormController>
      <FormController
        control={control}
        name="postalCode"
        label={t('Zip')}
        overrides={{ Root: { props: { className: 'col-span-2' } } }}
      >
        {({ field }) => <Input {...field} value={field.value ?? ''} />}
      </FormController>
      <FormController
        control={control}
        name="state"
        label={t('State')}
        overrides={{ Root: { props: { className: 'col-span-2' } } }}
      >
        {({ field }) => (
          <StateSelect
            countryCode={selectedCountryCode}
            value={field.value}
            onChange={({ option }) => field.onChange(option?.id ?? '')}
          />
        )}
      </FormController>
      </form>
    </>
  );
}

function Alert({ children }: { children: React.ReactNode }) {
  return (
    <div className="rounded-md bg-red-50 p-4">
      <div className="flex">
        <div className="flex-shrink-0">
          <AlertCircle className="h-5 w-5 text-red-400" aria-hidden="true" />
        </div>
        <div className="ml-3">{children}</div>
      </div>
    </div>
  );
}

function AlertTitle({ children }: { children: React.ReactNode }) {
  return (
    <h3 className="font-prompt text-sm font-medium text-red-800">{children}</h3>
  );
}

function AlertDescription({ children }: { children: React.ReactNode }) {
  return (
    <div className="mt-2 font-sans text-sm text-red-700">
      <p>{children}</p>
    </div>
  );
}

function AlertActions({ children }: { children: React.ReactNode }) {
  return (
    <div className="mt-4">
      <div className="-mx-2 -my-1.5 flex">{children}</div>
    </div>
  );
}

function VerfyingAddress<
  T extends FieldValues = FieldValues,
  TName extends FieldPath<T> = FieldPath<T>,
>({ control, path }: { control: Control<T>; path: TName }) {
  const isVerifying = useWatch({ control, name: path });
  const { t } = useLocale();
  if (isVerifying) {
    return (
      <div className="flex items-center space-x-1 text-gray-500">
        <Spinner $size={20} $borderWidth={3} />
        <span>{t('Verifying')}</span>
      </div>
    );
  }
  return null;
}

function RHFErrorMessageValue<
  T extends FieldValues = FieldValues,
  TName extends FieldPath<T> = FieldPath<T>,
>({
  control,
  field,
  children,
}: {
  control: Control<T>;
  field: TName;
  children: (message: string) => React.ReactNode;
}) {
  const { errors } = useFormState({ control, name: field, exact: true });
  const error = get(errors, field, '');

  if (!error) {
    return null;
  }
  const { message: messageFromRegister } = error;
  return <>{children(messageFromRegister)}</>;
}
